import React, { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate, useParams } from "react-router-dom"
import { ConfigScriptTemplate, RouterType } from "../data/models"
import useLazyQuery from "../hooks/useLazyQuery"

interface ITemplatePage {}
const TemplatePage: FunctionComponent<ITemplatePage> = () => {
	const navigate = useNavigate()
	const params = useParams()
	const templateId = params.templateId === "new" ? "" : params.templateId
	const templateQuery = useLazyQuery<void, RouterQueryResponse>(`${process.env.REACT_APP_KNIT_URL}config-script-templates?id=${templateId}`)
	const templateUrl = `${process.env.REACT_APP_KNIT_URL}config-script-templates/${templateId}`
	const patchTemplateQuery = useLazyQuery<Partial<ConfigScriptTemplate>, any>(templateUrl, "PATCH")
	const templatesUrl = `${process.env.REACT_APP_KNIT_URL}config-script-templates`
	const postTemplateQuery = useLazyQuery<Partial<ConfigScriptTemplate>, any>(templatesUrl, "POST")
	const [name, setName] = useState<string>("")
	const [type, setType] = useState<RouterType>()
	const [model, setModel] = useState<string>("")
	const [osVersion, setOsVersion] = useState<string>("")
	const [template, setTemplate] = useState<string>("")
	const [deleteVisible, setDeleteVisible] = useState(false)
	//
	useEffect(() => {
		if (templateId && !templateQuery.loading && !templateQuery.data) templateQuery.query()
	}, [templateId, templateQuery])
	//
	useEffect(() => {
		if (!name && templateQuery.data) {
			setName(templateQuery.data.configScriptTemplates[0].name)
			setType(templateQuery.data.configScriptTemplates[0].type)
			setModel(templateQuery.data.configScriptTemplates[0].model)
			setOsVersion(templateQuery.data.configScriptTemplates[0].osVersion)
			setTemplate(templateQuery.data.configScriptTemplates[0].template)
		}
	}, [name, templateQuery.data])
	//handle save
	const save = async () => {
		const configScriptTemplate = {
			name,
			type,
			model,
			osVersion,
			template,
		}
		if (templateId) {
			await patchTemplateQuery.query({ body: configScriptTemplate })
		} else {
			await postTemplateQuery.query({ body: configScriptTemplate })
		}
		navigate(-1)
	}
	//
	const error = templateQuery.error
	//
	if (templateQuery.loading) {
		return (
			<Container>
				<h2 className="text-center mt-4">Edit Config Script Template</h2>
				<div className="text-center">Loading</div>
			</Container>
		)
	}
	if (templateQuery.error) {
		return (
			<Container>
				<h2 className="text-center mt-4">Edit Config Script Template</h2>
				<Alert variant={"warning"}>{templateQuery.error}</Alert>
				<Col xs="auto">
					<Button variant="secondary" onClick={() => navigate(-1)} className="mb-4">
						Back
					</Button>
				</Col>
			</Container>
		)
	}
	return (
		<Container>
			<DeleteModal deleteVisible={deleteVisible} setDeleteVisible={setDeleteVisible} />
			<h2 className="text-center mt-4">Edit Config Script Template</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<Row xs={1} md={2} lg={4} className="g-4 mt-4">
				<Col>
					<Form.Group className="mb-3" controlId="name">
						<Form.Label>Name</Form.Label>
						<Form.Control type="text" required value={name} onChange={(e) => setName(e.target.value)} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="type">
						<Form.Label>Type</Form.Label>
						<Form.Select required value={type} onChange={(e) => setType(e.target.value as RouterType)}>
							<option></option>
							<option key="Property" value="Property">
								Property
							</option>
							<option key="Unit" value="Unit">
								Unit
							</option>
						</Form.Select>
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>Model</Form.Label>
						<Form.Control type="text" required value={model} onChange={(e) => setModel(e.target.value)} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="osVersion">
						<Form.Label>RouterOS Version</Form.Label>
						<Form.Control type="text" required value={osVersion} onChange={(e) => setOsVersion(e.target.value)} />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col>
					<Form.Group className="mb-3" controlId="template">
						<Form.Label>Template</Form.Label>
						<Form.Control as="textarea" rows={20} required value={template} onChange={(e) => setTemplate(e.target.value)} />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col xs="auto">
					<Button variant="secondary" onClick={() => navigate(-1)} className="mb-4">
						Back
					</Button>
				</Col>
				{templateId && (
					<Col xs="auto">
						<Button variant="outline-danger" onClick={() => setDeleteVisible(true)} className="mb-4">
							Delete
						</Button>
					</Col>
				)}
				<Col></Col>
				<Col xs="auto">
					<Button variant="primary" disabled={!name || !type} onClick={save} className="mb-4">
						Save
					</Button>
				</Col>
			</Row>
		</Container>
	)
}
export default TemplatePage

interface RouterQueryResponse {
	configScriptTemplates: ConfigScriptTemplate[]
}

interface IDeleteModal {
	deleteVisible: boolean
	setDeleteVisible: React.Dispatch<React.SetStateAction<boolean>>
}
const DeleteModal: FunctionComponent<IDeleteModal> = ({ deleteVisible, setDeleteVisible }) => {
	const navigate = useNavigate()
	const params = useParams()
	const templateId = params.templateId === "new" ? "" : params.templateId
	const templateUrl = `${process.env.REACT_APP_KNIT_URL}config-script-templates/${templateId}`
	const deleteTemplateQuery = useLazyQuery<void, any>(templateUrl, "DELETE")
	const deleteTemplate = async () => {
		await deleteTemplateQuery.query()
		navigate(-1)
	}
	return (
		<Modal show={deleteVisible} onHide={() => setDeleteVisible(false)}>
			<Modal.Header closeButton={!deleteTemplateQuery.loading}>
				<Modal.Title>Delete Router Script Template</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{deleteTemplateQuery.error && <Alert variant={"warning"}>{deleteTemplateQuery.error}</Alert>}
				This will delete the Router Script Template
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" disabled={deleteTemplateQuery.loading} onClick={() => setDeleteVisible(false)}>
					Cancel
				</Button>
				<Button variant="primary" disabled={deleteTemplateQuery.loading} onClick={deleteTemplate}>
					{deleteTemplateQuery.loading ? "Deleting" : "Delete"}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}
